"use client";

import { Card } from "@heroui/react";
import { Link } from "@/i18n/navigation";
import { APP_URL } from "@/lib/constants";
import {
  CreditCardFilled,
  HomeFilled,
  UserFilled,
  CodeFilled,
  ActivityFilled,
  HelpCircleFilled,
  BotFilled,
  FileTextFilled,
} from "@/components/icons";

const DESTINATIONS = [
  {
    title: "Home",
    description: "Head back to the homepage and start from the top.",
    href: "/",
    icon: HomeFilled,
  },
  {
    title: "Log in",
    description: "Sign in to your dashboard to manage keys and usage.",
    href: `${APP_URL}/login`,
    icon: UserFilled,
    external: true,
  },
  {
    title: "Pricing",
    description: "See credit rates and plans for every Encrata lookup.",
    href: "/pricing",
    icon: CreditCardFilled,
  },
  {
    title: "API reference",
    description: "Read the full reference for every Encrata endpoint.",
    href: "https://docs.encrata.com/api-reference/overview",
    icon: CodeFilled,
    external: true,
  },
  {
    title: "System status",
    description: "Check the current status of all Encrata services.",
    href: "https://status.encrata.com",
    icon: ActivityFilled,
    external: true,
  },
  {
    title: "Support",
    description: "Find answers or get in touch with our team.",
    href: "/contact",
    icon: HelpCircleFilled,
  },
  {
    title: "llms.txt",
    description: "The machine-readable index of everything we publish.",
    href: "/llms.txt",
    icon: BotFilled,
    external: true,
  },
  {
    title: "AI docs",
    description: "Markdown API notes written for agents and LLMs.",
    href: "/agents.md",
    icon: FileTextFilled,
    external: true,
  },
];

const CARD_CLASS =
  "group flex h-full flex-col gap-0! rounded-[20px] border-[0.5px]! border-border-secondary! bg-sidebar! p-6 text-left shadow-none transition-colors hover:bg-surface-sunken! dark:bg-card!";

export function NotFoundHero() {
  return (
    <>
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
          Error 404
        </p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight text-text-primary sm:text-5xl">
          Page not found
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-text-tertiary sm:text-base">
          We couldn&apos;t find the page you&apos;re looking for. Try one of these
          instead.
        </p>
      </div>

      <div className="heroui mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {DESTINATIONS.map(({ title, description, href, icon: Icon, external }) => (
          <Card
            key={title}
            variant="default"
            className={CARD_CLASS}
            render={(props: React.ComponentProps<"div">) =>
              external ? (
                <a
                  {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ) : (
                <Link {...(props as React.ComponentProps<typeof Link>)} href={href} />
              )
            }
          >
            <Icon className="size-5 text-brand" />
            <h2 className="mt-4 text-base font-semibold text-text-primary">{title}</h2>
            <p className="mt-1 text-sm leading-snug text-text-tertiary">{description}</p>
          </Card>
        ))}
      </div>
    </>
  );
}
